import { useEffect, useMemo, useState } from 'react';
import { copy } from './content';
import type { ProgramPreparation } from './domain/audition-plan';
import type { FinalLock, ManualChecks, OfficialProof, Program, Take } from './domain/model';
import { formatBytes, formatDuration } from './domain/preflight';
import { buildAuditionTakeTimeline } from './domain/take-timeline';
import { getRawMedia } from './platform/storage';

const verdictCopy = { pass: '통과', check: '확인 필요', fail: '수정 필요' } as const;

function LocalTakeThumbnail({ take, onPlay }: { take: Take; onPlay: (take: Take) => void }) {
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  const [url, setUrl] = useState<string>();

  useEffect(() => {
    let active = true;
    let objectUrl: string | undefined;
    getRawMedia(take.id).then((blob) => {
      if (!active) return;
      if (!blob) { setState('missing'); return; }
      objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
      setState('ready');
    }).catch(() => { if (active) setState('error'); });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [take.id]);

  return <div className={`timeline-thumbnail is-${state}`}>
    {state === 'ready' && url ? <video aria-label={`${take.title} 기기 영상 미리보기`} src={`${url}#t=0.1`} muted playsInline preload="metadata" /> : <div className="thumbnail-fallback" role="status">
      <span aria-hidden="true">{state === 'loading' ? '···' : state === 'missing' ? '⌁' : '!'}</span>
      <strong>{state === 'loading' ? '기기 원본 확인 중' : state === 'missing' ? copy.takeTimeline.mediaMissing : copy.takeTimeline.mediaError}</strong>
      <small>{state === 'error' ? '브라우저 저장소 권한·저장 상태를 확인하세요.' : '파일 정보와 점검 기록은 그대로 볼 수 있어요.'}</small>
    </div>}
    <button className="thumbnail-play" onClick={() => onPlay(take)}>기기에서 재생</button>
  </div>;
}

export function TakeTimelineView({ program, takes, lock, manual, preparation, proof, onUpdateTake, onPlay, onOpenTakes }: {
  program: Program;
  takes: Take[];
  lock?: FinalLock;
  manual: ManualChecks;
  preparation: ProgramPreparation;
  proof?: OfficialProof;
  onUpdateTake: (take: Take) => void;
  onPlay: (take: Take) => void;
  onOpenTakes: () => void;
}) {
  const timeline = useMemo(() => buildAuditionTakeTimeline({ program, takes, lock, manual, preparation, proof }), [program, takes, lock, manual, preparation, proof]);
  const selectedIndex = Math.max(0, timeline.takes.findIndex((item) => item.isSelected));
  const [slide, setSlide] = useState(selectedIndex);
  const safeSlide = Math.min(slide, Math.max(0, timeline.takes.length - 1));
  const current = timeline.takes[safeSlide];

  return <main id="main" className="workspace-page take-timeline-page">
    <section className="timeline-hero">
      <div><span className="eyebrow">촬영 영상 비교</span><h1>{copy.takeTimeline.headline}</h1><p>{copy.takeTimeline.description}</p></div>
      <div className="timeline-count"><strong>{timeline.takes.length}</strong><span>촬영한 영상</span><small>{timeline.comparisonReady ? '한 장씩 비교 가능' : '두 번째 영상부터 비교 가능'}</small></div>
    </section>

    <ol className="audition-milestones" aria-label="오디션 준비와 결과 기록 순서">
      {timeline.milestones.map((item, index) => <li key={item.id} className={item.state === 'done' ? 'is-done' : ''}>
        <span>{String(index + 1).padStart(2, '0')}</span><div><strong>{item.label}</strong><small>{item.detail}</small></div>
      </li>)}
    </ol>

    {!current ? <section className="timeline-empty">
      <div className="empty-score" aria-hidden="true">𝄞</div><h2>아직 비교할 영상이 없어요.</h2><p>첫 영상을 불러오면 촬영 시각·점검 결과·선택 이유가 이 흐름에 쌓여요.</p><button className="primary" onClick={onOpenTakes}>첫 영상 불러오기</button>
    </section> : <>
      <section className="take-slide" aria-live="polite" aria-label={`${safeSlide + 1}번째 촬영 영상 기록`}>
        <LocalTakeThumbnail key={current.take.id} take={current.take} onPlay={onPlay} />
        <div className="take-slide-copy">
          <div className="take-slide-kicker"><span>영상 {String(current.order).padStart(2, '0')}</span><em className={`verdict-${current.verdict}`}>{verdictCopy[current.verdict]}</em>{current.isSelected && <b>보낼 영상</b>}</div>
          <h2>{current.take.title}</h2>
          <p className="take-time">{new Date(current.take.createdAt).toLocaleString('ko-KR')} · {current.take.durationSeconds ? formatDuration(current.take.durationSeconds) : '길이 확인 필요'} · {formatBytes(current.take.bytes)}</p>
          <div className="check-tally" aria-label="검사 결과 요약"><span><b>{current.checks.pass}</b> 통과</span><span><b>{current.checks.check}</b> 확인 필요</span><span><b>{current.checks.fail}</b> 수정 필요</span></div>
          <label className="selection-reason">이 영상을 남긴 이유
            <textarea value={current.take.note} placeholder="예: 시작 여백이 요강과 맞고, 다음에는 카메라를 10cm 낮춘다." onChange={(event) => onUpdateTake({ ...current.take, note: event.target.value })} />
            <small>연주 점수가 아니라 내가 보고 들은 사실과 다음 촬영의 한 가지를 적어요.</small>
          </label>
        </div>
      </section>
      <div className="slide-controls" aria-label="촬영 영상 이동">
        <button className="secondary" disabled={safeSlide === 0} onClick={() => setSlide((value) => Math.max(0, value - 1))}>이전 영상</button>
        <div>{timeline.takes.map((item, index) => <button key={item.take.id} aria-label={`${index + 1}번째 영상 보기`} aria-current={index === safeSlide ? 'true' : undefined} className={index === safeSlide ? 'active' : ''} onClick={() => setSlide(index)} />)}</div>
        <button className="secondary" disabled={safeSlide === timeline.takes.length - 1} onClick={() => setSlide((value) => Math.min(timeline.takes.length - 1, value + 1))}>다음 영상</button>
      </div>
      {!timeline.comparisonReady && <aside className="comparison-locked"><strong>비교는 영상 두 개부터 열립니다.</strong><p>연주를 점수로 평가하지 않고, 다음 촬영에서 달라진 사실을 나란히 보기 위한 기능이에요.</p><button className="secondary" onClick={onOpenTakes}>다음 영상 불러오기</button></aside>}
      <section className="take-chronology" aria-labelledby="take-chronology-title">
        <div><span className="eyebrow">시간순 기록</span><h2 id="take-chronology-title">보내지 않은 영상도 이유와 함께 남아요.</h2></div>
        <ol>{timeline.takes.map((item) => <li key={item.take.id} className={item.isSelected ? 'is-selected' : ''}>
          <time dateTime={item.take.createdAt}>{new Date(item.take.createdAt).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</time>
          <div><strong>{item.take.title}</strong><p>{item.selectionReason || '남긴 이유를 아직 적지 않았어요.'}</p></div>
          <span>{item.isSelected ? '보낼 영상' : verdictCopy[item.verdict]}</span>
        </li>)}</ol>
      </section>
    </>}

    <aside className="timeline-safety"><strong>이 비교가 하지 않는 일</strong><p>{copy.takeTimeline.safety}</p></aside>
  </main>;
}
